import { useTranslation } from "react-i18next";
import { CheckCircle, HelpCircle } from "lucide-react";
import ServicePageLayout from "@/components/ServicePageLayout";

const pageKeys = [
  "activity",
  "certification",
  "consulting",
  "courses",
  "ecommerce",
  "interview",
  "ipIncubation",
  "matrix",
  "operations",
  "teamExpression",
  "webDev",
] as const;

type ServicePageKey = (typeof pageKeys)[number];

interface SectionItem {
  title: string;
  desc: string;
}

interface SectionBlock {
  title: string;
  items: SectionItem[];
}

interface FaqItem {
  q: string;
  a: string;
}

interface ServiceDetailContent {
  introTitle: string;
  introParagraphs: string[];
  emphasis: string;
  sections: SectionBlock[];
  fitTitle: string;
  fitItems: string[];
  faqTitle: string;
  faqs: FaqItem[];
  outro: string[];
}

const isPageKey = (key: string): key is ServicePageKey => pageKeys.includes(key as ServicePageKey);

const ServiceDetailPage = ({ pageKey }: { pageKey: ServicePageKey | string }) => {
  const { t, i18n } = useTranslation();
  const safeKey = isPageKey(pageKey) ? pageKey : "consulting";
  const detailKey = i18n.exists(`serviceDetail.${safeKey}.introTitle`) ? `serviceDetail.${safeKey}` : "serviceDetail.common";
  const data = t(detailKey, { returnObjects: true }) as ServiceDetailContent;

  return (
    <ServicePageLayout
      title={t(`pages.${safeKey}.title`)}
      subtitle={t(`pages.${safeKey}.subtitle`)}
      ctaText={t(`pages.${safeKey}.cta`)}
    >
      <div className="max-w-4xl mx-auto">
        <div className="ttk-service-panel rounded-3xl p-8 md:p-10 mb-8 reveal-up">
          <h3 className="text-2xl font-semibold tracking-tight mb-4">{data.introTitle}</h3>
          <div className="space-y-3 text-muted-foreground leading-7">
            {data.introParagraphs.map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
          <p className="text-lg font-bold gradient-gold-text text-center mt-5">{data.emphasis}</p>
        </div>

        {data.sections.map((section, i) => (
          <div
            key={`${section.title}-${i}`}
            className="ttk-service-panel rounded-3xl p-8 md:p-10 mb-8 reveal-up"
            style={{ animationDelay: `${i * 0.05}s` }}
          >
            <h3 className="text-2xl font-semibold tracking-tight mb-6">{section.title}</h3>
            <div className="space-y-6">
              {section.items.map((item, j) => (
                <div key={`${item.title}-${j}`} className="pb-4 last:pb-0">
                  <h4 className="font-semibold text-foreground mb-1.5">{item.title}</h4>
                  <p className="text-sm text-muted-foreground leading-7">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="ttk-service-panel rounded-3xl p-6 md:p-8 mb-8 reveal-up">
          <h4 className="font-semibold text-xl tracking-tight mb-4">{data.fitTitle}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {data.fitItems.map((item, i) => (
              <div key={`${item}-${i}`} className="ttk-service-chip rounded-full px-4 py-2.5 flex items-center gap-2 text-sm">
                <CheckCircle className="w-3 h-3 text-foreground" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="ttk-service-panel rounded-3xl p-8 md:p-10 mb-8 reveal-up">
          <h3 className="text-2xl font-semibold tracking-tight mb-6 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-foreground" /> {data.faqTitle}
          </h3>
          <div className="space-y-3">
            {data.faqs.map((item, i) => (
              <div key={`${item.q}-${i}`} className="ttk-service-chip rounded-2xl p-4">
                <h4 className="font-semibold text-sm mb-1.5 text-foreground">{item.q}</h4>
                <p className="text-sm text-muted-foreground leading-7">{item.a}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center py-8 reveal-up">
          <p className="text-lg mb-2">{data.outro[0]}</p>
          <p className="text-xl font-bold gradient-gold-text">{data.outro[1]}</p>
        </div>
      </div>
    </ServicePageLayout>
  );
};

export default ServiceDetailPage;
